import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import useTitanApp from "../../hooks/useTitanApp";
import useTitanTeam from "../../hooks/useTitanTeam";

export interface TimeBlock {
  from: string;
  to: string;
}

export interface MemberAvailability {
  monday: { enabled: boolean; timeBlocks: TimeBlock[] };
  tuesday: { enabled: boolean; timeBlocks: TimeBlock[] };
  wednesday: { enabled: boolean; timeBlocks: TimeBlock[] };
  thursday: { enabled: boolean; timeBlocks: TimeBlock[] };
  friday: { enabled: boolean; timeBlocks: TimeBlock[] };
  saturday: { enabled: boolean; timeBlocks: TimeBlock[] };
  sunday: { enabled: boolean; timeBlocks: TimeBlock[] };
}

interface BackendUserNotification {
  type: number;
  boolValue: boolean | null;
  stringValue: string | null;
  jsonValue: string | null;
}

interface FrontendNotifications {
  taskAssigned: boolean;
  taskUpdated: boolean;
  labResults: boolean;
  bookingMade: {
    enabled: boolean;
    services: Record<string, boolean>;
  };
  bookingRescheduledOrCancelled: {
    enabled: boolean;
    services: Record<string, boolean>;
  };
}

export interface Member {
  id: string;
  title: string;
  forename: string;
  surname: string;
  email: string;
  role: string;
  isClinician: boolean;
  active: boolean;
  qualifications?: string[];
  registrations?: string[];
  servicesProvided?: string[];
  pharmacies?: string[];
  notifications?: FrontendNotifications;
  availability?: MemberAvailability;
  companyID: string;
  startDate: string;
  endDate: string;
}
interface PendingInvite {
  id: string;
  email: string;
  role: string;
  selectedTeams: string[];
  message?: string;
  invitedBy: string;
  invitedAt: string;
  lastSentAt: string;
}

export const availableQualifications = [
  "MPharm (Master of Pharmacy)",
  "BPharm (Bachelor of Pharmacy)",
  "PharmD (Doctor of Pharmacy)",
  "MSc Clinical Pharmacy",
  "PGDip Pharmacy Practice",
  "Independent Prescriber",
  "Supplementary Prescriber",
];

export const availableRegistrations = [
  "GPhC Registration",
  "PSNI Registration",
  "Independent Prescriber Registration",
  "Supplementary Prescriber Registration",
  "Controlled Drug Registration",
  "Advanced Practice Registration",
];

export const availableServices = [
  "Ear Wax Removal",
  "Blood Pressure Check",
  "Vaccination Administration",
  "Medicine Use Review",
  "New Medicine Service",
  "Travel Health Consultation",
  "Emergency Contraception",
  "Smoking Cessation",
];

export const availablePharmacies = [
  "TITAN Pharmacy",
  "City Centre Pharmacy",
  "Westbury Pharmacy",
  "Clifton Pharmacy",
  "Redfield Pharmacy",
];
export const Titles = ["", "Mr.", "Mrs.", "Miss", "Ms.", "Dr.", "Prof."];

export const dayNames = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

export const dayLabels = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const dayMap: {
  [key: number]: keyof typeof defaultMemberTemplate.availability;
} = {
  1: "monday",
  2: "tuesday",
  3: "wednesday",
  4: "thursday",
  5: "friday",
  6: "saturday",
  0: "sunday",
};
export interface InviteData {
  email: string;
  selectedTeams: string[];
  role: string;
  message: string;
  roleId: string;
  startDate?: string; // Add this
  endDate?: string; // Add this
}
export interface Team {
  id: string;
  name: string;
  description: string;
  color: string;
  active: boolean;
}

const mapAvailability = (userWeekAvailability: any[]) => {
  const availability: any = {
    monday: { enabled: false, timeBlocks: [] },
    tuesday: { enabled: false, timeBlocks: [] },
    wednesday: { enabled: false, timeBlocks: [] },
    thursday: { enabled: false, timeBlocks: [] },
    friday: { enabled: false, timeBlocks: [] },
    saturday: { enabled: false, timeBlocks: [] },
    sunday: { enabled: false, timeBlocks: [] },
  };

  if (userWeekAvailability && Array.isArray(userWeekAvailability)) {
    userWeekAvailability.forEach((day: any) => {
      const dayName = dayMap[day.dayOfWeek];
      if (dayName) {
        availability[dayName] = {
          enabled: day.slots && day.slots.length > 0,
          timeBlocks: day.slots.map((slot: any) => ({
            from: slot.startTime,
            to: slot.endTime,
          })),
        };
      }
    });
  }

  return availability;
};

export const defaultMemberTemplate = {
  title: "Mr.",
  forename: "",
  surname: "",
  email: "",
  isClinician: false,
  qualifications: [],
  registrations: [],
  role: "",
  servicesProvided: [],
  pharmacies: [],
  notifications: {
    taskAssigned: false,
    taskUpdated: false,
    labResults: false,
    bookingMade: { enabled: true, services: {} },
    bookingRescheduledOrCancelled: { enabled: true, services: {} },
  },
  availability: {
    monday: {
      enabled: true,
      timeBlocks: [
        { from: "09:00", to: "17:00" },
        { from: "09:00", to: "17:00" },
      ],
    },
    tuesday: { enabled: true, timeBlocks: [{ from: "09:00", to: "17:00" }] },
    wednesday: { enabled: true, timeBlocks: [{ from: "09:00", to: "17:00" }] },
    thursday: { enabled: true, timeBlocks: [{ from: "09:00", to: "17:00" }] },
    friday: { enabled: true, timeBlocks: [{ from: "09:00", to: "17:00" }] },
    saturday: { enabled: false, timeBlocks: [] },
    sunday: { enabled: false, timeBlocks: [] },
  },
  roleId: "",
  companyID: "",
  startDate: "", // Add this
  endDate: "", // Add this
};

export const useMembersData = () => {
  const [initialMembers, setInitialMembers] = useState<Member[]>([]);
  const {
    memberState,
    appState,
    companyServiceBillingState,
    generalsState,
    locationState,
  } = useTitanApp();
  const {
    membersData,
    isMemberLoading,
    defaultPharmacyServices,
    pendingInvites,
    isPendingInvitesLoading,
  } = memberState || {};
  const {
    darkMode,
    features,
    companyDetails,
    companyId,
    currentCompanyId,
    currentUser,
  } = appState || {};
  const { companySubscriptionStatus } = companyDetails || {};
  const { generalData } = generalsState || {};
  const { companySubscription } = companyServiceBillingState || {};
  const { allPharmacies } = locationState || {};

  const { teamState } = useTitanTeam();
  const { teams } = teamState || {};
  const { teamRolesAll, isRolesLoading, isTeamMemberInviteLoading } = teamState;
  const { ifBillingEnabled } = features || {};
  const convertNotificationsFromBackend = (
    notifications?: BackendUserNotification[] | null,
  ): FrontendNotifications => {
    if (!notifications || !Array.isArray(notifications)) {
      return {
        taskAssigned: false,
        taskUpdated: false,
        labResults: false,
        bookingMade: { enabled: true, services: {} },
        bookingRescheduledOrCancelled: { enabled: true, services: {} },
      };
    }

    const result: FrontendNotifications = {
      taskAssigned: false,
      taskUpdated: false,
      labResults: false,
      bookingMade: { enabled: false, services: {} },
      bookingRescheduledOrCancelled: { enabled: false, services: {} },
    };

    notifications.forEach((notif) => {
      switch (notif.type) {
        case 1:
          result.taskAssigned = notif.boolValue ?? false;
          break;
        case 2:
          result.taskUpdated = notif.boolValue ?? false;
          break;
        case 3:
          result.labResults = notif.boolValue ?? false;
          break;
        case 4:
          if (notif.jsonValue) {
            try {
              result.bookingMade = JSON.parse(notif.jsonValue);
            } catch {}
          }
          break;
        case 5:
          if (notif.jsonValue) {
            try {
              result.bookingRescheduledOrCancelled = JSON.parse(
                notif.jsonValue,
              );
            } catch {}
          }
          break;
      }
    });

    return result;
  };
  useEffect(() => {
    if (membersData) {
      const mappedMembers: Member[] = membersData.map((m: any) => {
        return {
          id: m.userId,
          companyID: m.companyID || "",
          email: m.emailAddress || "",
          forename: m.firstName || "",
          gender: m.gender ?? null,
          surname: m.lastName || "",
          userImage: m.userImage ?? "",
          isNew: m.isNew ?? null,
          title: m.title != null ? m.title.toString() : "0",
          isClinician: m.isClinician || false,
          qualifications: m.qualification
            ? m.qualification.split(",").map((q: string) => q.trim())
            : [],
          registrations: m.registrations
            ? m.registrations
                .split(",")
                .map((r: string) => r.trim())
                .filter((r) => r.length > 0)
            : [],
          accountType: m.accountType || 0,
          isVerified: m.isVerified ?? null,
          active: m.isActive || false,
          newCompanyID: m.newCompanyID ?? null,
          lastLogin: m.lastLogin ?? null,
          userStatus: m.userStatus ?? null,
          gphcNumber: m.gphcNumber ?? null,
          phoneNumber: m.phoneNumber ?? null,
          roleId: m.role?._id || "",
          role: m.role?.roleName || "",
          servicesProvided: m.services
            ? m.services.map((s: any) => s._id || "")
            : [],
          pharmacies: m.pharmacies
            ? m.pharmacies.map((p: any) => p.pharmacyID || "")
            : [],
          notifications: convertNotificationsFromBackend(m.notifications),
          availability: mapAvailability(m.userWeekAvailability || []),
          startDate: m.startDate || "",
          endDate: m.endDate || "",
        };
      });
      setInitialMembers(mappedMembers);
    }
  }, [membersData]);
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    watch,
    setError,
    setValue,
  } = useForm({
    defaultValues: {
      email: "",
      selectedTeams: [] as string[],
      roleId: "",
      message: "",
    },
    mode: "onChange",
  });
  useEffect(() => {
    const subscription = watch((values) => {
      setFormData((p) => ({
        ...p,
        email: values.email ?? "",
        selectedTeams: values.selectedTeams ?? [],
        roleId: values.roleId ?? "",
        role: teamRolesAll.find((r) => r._id === values.roleId)?.roleName ?? "",
        message: values.message ?? "",
      }));
    });
    return () => subscription.unsubscribe();
  }, [watch, teamRolesAll]);

  useEffect(() => {
    register("selectedTeams", {
      validate: (v: any) =>
        (Array.isArray(v) && v.length > 0) || "Select at least 1 team",
    });
    register("roleId", { required: "Role is required" });
    register("message");
  }, [register]);

  const [formData, setFormData] = useState<InviteData>({
    email: "",
    selectedTeams: [],
    role: "",
    message: "",
    roleId: "",
  });

  const updateField = (field: keyof InviteData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleToggleTeam = (teamId: string) => {
    const current: string[] = (watch("selectedTeams") as string[]) || [];
    const updated = current.includes(teamId)
      ? current.filter((id) => id !== teamId)
      : [...current, teamId];
    setValue("selectedTeams", updated, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };
  const handleRoleChange = (id: string) => {
    setValue("roleId", id, { shouldValidate: true, shouldDirty: true });
  };
  const handleMessageChange = (val: string) => {
    setValue("message", val, { shouldDirty: true });
  };

  const userRole = currentUser?.role?.roleName?.toLowerCase();
  const isAdminUser = userRole === "admin";

  let filteredRoles = teamRolesAll?.filter(
    (r) => r.roleName?.toLowerCase() !== "super admin",
  );
  return {
    initialMembers,
    isMemberLoading,
    darkMode,
    availableRoles: filteredRoles,
    defaultPharmacyServices,
    allPharmacies,
    teams,
    isTeamMemberInviteLoading,
    formData,
    errors,
    register,
    isValid,
    isDirty,
    handleSubmit,
    handleRoleChange,
    handleToggleTeam,
    handleMessageChange,
    companySubscription,
    ifBillingEnabled,
    companySubscriptionStatus,
    generalData,
    companyId,
    currentCompanyId,
    pendingInvites,
    isPendingInvitesLoading,
    setError,
    setFormData,
  };
};
